import { theme } from '@loop/ui'
import { Close, WarningRounded } from '@mui/icons-material'
import { AppBar, Box, Button, Dialog, Divider, Fade, IconButton, Toolbar, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { BillingActivePhase, BillingActivePhaseStyles, useBillingContext } from 'src/context/BillingContext'
import { drawerCloseWidthPx, drawerWidthPx } from './components/PrivateLayoutDrawer'

const BillingBanner = ({
  drawerState,
  billingBarOffsetHeight,
  setBillingBarOffsetHeight
}: {
  drawerState: boolean
  billingBarOffsetHeight: number
  setBillingBarOffsetHeight: (height: number) => void
}) => {
  const { activeBillingConfig, isBillingConfigExpired, billingBannerOpen, setBillingBannerOpen, billingDialogOpen, setBillingDialogOpen } = useBillingContext()

  const billingBarRef = React.useRef<HTMLElement>(null)

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setBillingBarOffsetHeight(billingBarRef.current?.offsetHeight || 0)
    })

    if (billingBarRef.current) resizeObserver.observe(billingBarRef.current)
  }, [billingBarRef?.current])

  return (
    <>
      <Dialog
        open={billingDialogOpen}
        onClose={() => {
          setBillingDialogOpen(false)
          setBillingBannerOpen(true)
        }}
        fullWidth
        sx={{
          zIndex: 10000
        }}
        PaperProps={{
          style: {
            maxWidth: '700px'
          }
        }}>
        <Box p={3}>
          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end">
            <IconButton
              onClick={() => {
                setBillingDialogOpen(false)
                setBillingBannerOpen(true)
              }}>
              <Close />
            </IconButton>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            gap={2}>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              alignItems="center">
              <WarningRounded sx={{ color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.secondaryColor, fontSize: 60 }} />
              <Typography
                fontSize={16}
                color="#000"
                fontWeight={600}>
                Payment Alert!
              </Typography>
            </Box>

            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}>
              <Typography
                fontSize={14}
                color="#000"
                textAlign="center">
                {BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.texts(activeBillingConfig).first}
              </Typography>

              <Typography
                fontSize={14}
                color="#000"
                textAlign="center">
                {BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.texts(activeBillingConfig).second}
              </Typography>
            </Box>

            <Divider sx={{ width: '100%' }} />

            <Box
              width="100%"
              display="flex"
              alignItems="center"
              gap={1}>
              <Button
                onClick={() => {
                  setBillingDialogOpen(false)
                  setBillingBannerOpen(true)
                }}
                sx={{
                  py: 2,
                  width: '100%',
                  bgcolor: '#7676761A',
                  color: '#000',
                  '&:hover': {
                    bgcolor: '#76767660'
                  }
                }}>
                Remind Me Later
              </Button>
              <Button
                onClick={() => window.open(activeBillingConfig?.paymentLink, '_blank')}
                sx={{
                  py: 2,
                  width: '100%'
                }}>
                Complete Payment
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {activeBillingConfig && !isBillingConfigExpired && (
        <>
          <Fade in={billingBannerOpen}>
            <AppBar
              ref={billingBarRef}
              position="fixed"
              sx={{
                display: billingBannerOpen ? 'block' : 'none',
                backgroundColor: 'white',
                bgcolor: 'white',
                borderBottom: '1px solid #E6E6E6',
                top: '67px',
                width: {
                  sm: drawerState ? `calc(100vw - ${drawerWidthPx})` : `calc(100vw - ${drawerCloseWidthPx})`,
                  xs: '100%'
                },
                left: {
                  sm: drawerState ? drawerWidthPx : drawerCloseWidthPx,
                  xs: 'auto'
                },
                transition: theme.transitions.create(['width', 'left'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen
                })
              }}>
              <Box
                px={3}
                py={1}>
                <Box
                  bgcolor={BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.backgroundColor}
                  borderRadius={1}
                  p={1}>
                  <Box
                    border={`1px solid ${BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.secondaryColor}`}
                    borderRadius={1}
                    p={1}
                    display="flex"
                    gap={1}
                    width="100%">
                    <Box>
                      <WarningRounded sx={{ color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.secondaryColor }} />
                    </Box>
                    <Box width="100%">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        height="20px">
                        <Typography
                          fontSize={12}
                          color={BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor}>
                          {BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.texts(activeBillingConfig).first}
                        </Typography>

                        <IconButton
                          onClick={() => setBillingBannerOpen(false)}
                          sx={{
                            display: activeBillingConfig?.activePhase === BillingActivePhase.PHASE1 ? 'flex' : 'none',
                            borderRadius: '4px',
                            bgcolor: 'transparent',
                            color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor,
                            height: '24px',
                            width: '24px',
                            '&:hover': {
                              bgcolor: '#00000010',
                              color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor
                            }
                          }}>
                          <Close sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Box>

                      <Typography
                        fontSize={12}
                        color={BillingActivePhaseStyles[activeBillingConfig?.activePhase].primaryColor}
                        height="20px">
                        {BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.texts(activeBillingConfig).second}
                      </Typography>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        height="20px">
                        <Typography
                          fontSize={12}
                          color={BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor}>
                          {BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.texts(activeBillingConfig).third}
                        </Typography>

                        <Button
                          disableElevation
                          onClick={() => window.open(activeBillingConfig?.paymentLink, '_blank')}
                          sx={{
                            bgcolor: 'transparent',
                            color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor,
                            border: `1px solid ${BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor}`,
                            py: 0,
                            height: '24px',
                            fontSize: 12,
                            fontWeight: 400,
                            borderRadius: '4px',
                            px: 2,
                            '&:hover': {
                              bgcolor: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor,
                              border: `1px solid ${BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor}`,
                              color: '#fff'
                            }
                          }}>
                          Complete Payment
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AppBar>
          </Fade>

          <Toolbar sx={{ height: billingBarOffsetHeight, minHeight: `${billingBarOffsetHeight}px !important` }} />
        </>
      )}
    </>
  )
}

export default BillingBanner
